/**
 * Cache mémoire partagé par les pages « parc d'hôtels » (/hotels, /lunishower).
 *
 * Ces pages sont démontées à chaque changement d'onglet : sans cache, revenir dessus
 * relançait `/home/list` + `/home/stats` et repeignait les 83 lignes à partir de zéro.
 * Le cache conserve la liste déjà fusionnée entre deux montages : au retour, la page
 * s'affiche immédiatement, et le réseau n'est resollicité que si les données ont dépassé
 * leur durée de fraîcheur.
 *
 * Volontairement en mémoire seulement (pas de localStorage) : on veut survivre à une
 * navigation, pas à un rechargement complet de l'application.
 */

const store = new Map();
const loadingKeys = new Set();

/** Durée pendant laquelle une liste déjà chargée est réutilisée sans requête réseau. */
export const CACHE_TTL_MS = 60000;

export const readCache = (key) => store.get(key);

export const isFresh = (entry, ttl = CACHE_TTL_MS) => Boolean(entry) && Date.now() - entry.fetchedAt < ttl;

/**
 * Met à jour les données sans toucher à la fraîcheur.
 * Utilisé par les mutations locales (tag, statut de feature) : modifier une ligne ne doit
 * pas faire passer pour « fraîche » une liste chargée il y a dix minutes.
 */
export const setCacheData = (key, data) => {
  const entry = store.get(key);
  store.set(key, { data, fetchedAt: entry ? entry.fetchedAt : Date.now() });
};

/** Enregistre le résultat d'un vrai chargement réseau : relance le compteur de fraîcheur. */
export const setCacheFetched = (key, data) => {
  store.set(key, { data, fetchedAt: Date.now() });
};

export const clearHotelsCache = () => {
  store.clear();
  loadingKeys.clear();
};

/** Un chargement est-il déjà en cours pour cette clé ? */
export const isLoadingKey = (key) => loadingKeys.has(key);

/**
 * Marque la clé comme « en cours de chargement » le temps de `run`.
 *
 * Sert à dédupliquer : une revalidation de fond déclenchée alors qu'un chargement est déjà
 * en vol est simplement abandonnée par l'appelant. On ne partage volontairement pas la
 * promesse elle-même — elle est annulable au démontage, et un appelant qui n'a rien
 * demandé hériterait de l'annulation d'un autre.
 */
export const runExclusive = async (key, run) => {
  loadingKeys.add(key);
  try {
    return await run();
  } finally {
    loadingKeys.delete(key);
  }
};

/**
 * Vrai pour une requête annulée par nous (démontage), pas pour une vraie erreur réseau.
 * Couvre axios (`ERR_CANCELED`) et `fetch` (`AbortError`).
 */
export const isAbortError = (error) =>
  Boolean(error) && (error.code === "ERR_CANCELED" || error.name === "CanceledError" || error.name === "AbortError");

/** Égalité structurelle sur les petits objets JSON renvoyés par l'API (1 à 2 niveaux). */
const isDeepEqual = (a, b) => {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== "object" || typeof b !== "object") return false;

  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;

  return aKeys.every((key) => isDeepEqual(a[key], b[key]));
};

/**
 * Fusionne des correctifs indexés par id d'hôtel dans la liste courante, **en préservant
 * les références** des hôtels dont rien ne change.
 *
 * C'est le point clé pour `React.memo` : `{ ...hotel, ...patch }` appliqué à tout le monde
 * recréait les 83 objets à chaque réponse, donc re-rendait les 83 lignes même quand les
 * valeurs étaient identiques. Ici, une revalidation qui ne change rien renvoie le tableau
 * précédent tel quel — `setHotels` court-circuite alors le rendu.
 *
 * @param {Array}  hotels      liste courante
 * @param {object} patchesById correctifs `{ [hotelId]: { ...champs } }`
 * @returns {Array} la liste d'origine si rien n'a changé, sinon une nouvelle liste
 */
export const mergeHotelPatches = (hotels, patchesById) => {
  if (!patchesById) return hotels;

  let changed = false;
  const next = hotels.map((hotel) => {
    const patch = patchesById[hotel.id];
    if (!patch) return hotel;

    const isAlreadyApplied = Object.keys(patch).every((field) => isDeepEqual(hotel[field], patch[field]));
    if (isAlreadyApplied) return hotel;

    changed = true;
    return { ...hotel, ...patch };
  });

  return changed ? next : hotels;
};

/** Champs alimentés par `/home/stats`, à préserver quand seule la liste est rechargée. */
const pickStats = (hotel) => {
  const stats = {};
  if (hotel.good_percentages !== undefined) stats.good_percentages = hotel.good_percentages;
  if (hotel.room_status !== undefined) stats.room_status = hotel.room_status;
  if (hotel.last_check !== undefined) stats.last_check = hotel.last_check;
  return stats;
};

/** Même logique pour un remplacement complet de liste (revalidation de `/home/list`). */
export const mergeHotelList = (previousHotels, incomingHotels) => {
  const previousById = new Map(previousHotels.map((hotel) => [hotel.id, hotel]));

  // Un hôtel ajouté ou retiré change forcément la liste, même si toutes les lignes communes
  // sont identiques. On part de ce constat, puis on compare ligne à ligne : les hôtels
  // inchangés gardent leur référence (et leurs stats) dans tous les cas.
  let changed = previousHotels.length !== incomingHotels.length;
  const next = incomingHotels.map((incoming) => {
    const previous = previousById.get(incoming.id);
    if (!previous) {
      changed = true;
      return incoming;
    }

    // `/home/list` ne renvoie pas les champs de stats : les conserver évite de vider les
    // colonnes déjà remplies pendant une revalidation de fond.
    const merged = { ...previous, ...incoming, ...pickStats(previous) };
    if (isDeepEqual(previous, merged)) return previous;

    changed = true;
    return merged;
  });

  return changed ? next : previousHotels;
};
